import * as controllers from 'controllers'
import { ClusterShipperDto } from 'dtos'
import { ClusterShipperNeedsClusterDeliveredToThirdPartyConversionCompanyPhotoProps } from '.'
import { useDialogState, useLoadingState } from 'hooks'
import ClusterShippersThirdPartConversionDeliveryAddEditDialog from '../ClusterShippersThirdPartyConversionDeliveryAddEditDialog'
import { enqueueSnackbar } from 'notistack'
import { Button, Card, CardContent, Divider, Grid } from '@mui/material'
import NeedsClusterDeliveredToThirdPartyConversionCompanyStepper from './NeedsClusterDeliveredToThirdPartyConversionCompanyStepper'
import NeedsThirdPartyConversionDeliveryPhotoCard from './NeedsThirdPartyConversionDeliveryPhotoCard'
import { ArrowRight } from '@mui/icons-material'
import { GreenText } from 'components'

interface Step1NeedsClusterDeliveredToThirdPartyConversionCompanyPhotoProps
  extends ClusterShipperNeedsClusterDeliveredToThirdPartyConversionCompanyPhotoProps {
  onBack: () => void
  onNext: () => void
  clusterShipperDetails: ClusterShipperDto
  clusterShipperGuid?: string
}

export default function Step1ThirdPartyConversionDeliveryPhoto({
  onNext,
  clusterShipperDetails,
  clusterShipperGuid,
  thirdPartyConversionDeliveryPhoto,
  setThirdPartyConversionDeliveryPhoto
}: Step1NeedsClusterDeliveredToThirdPartyConversionCompanyPhotoProps) {
  const [loadingState, setLoadingState] = useLoadingState({
    isUpdatingThirdPartyConversionDeliveryPhoto: false,
    isDeletingThirdPartyConversionDeliveryPhoto: false
  })

  const [dialogState, setDialogState] = useDialogState({
    thirdPartyConversionDeliveryPhotoDialogOpen: false
  })

  const hasThirdPartyConversionDeliveryPhoto = Boolean(
    thirdPartyConversionDeliveryPhoto.clusterShipperThirdPartyConversionDeliveryPhotoFileName
  )

  const allPhotosUploaded = hasThirdPartyConversionDeliveryPhoto

  return (
    <>
      <ClusterShippersThirdPartConversionDeliveryAddEditDialog
        initialValues={thirdPartyConversionDeliveryPhoto}
        isLoading={loadingState.isUpdatingThirdPartyConversionDeliveryPhoto}
        onClose={() => {
          setDialogState('thirdPartyConversionDeliveryPhotoDialogOpen', false)
        }}
        onSave={values => {
          setLoadingState('isUpdatingThirdPartyConversionDeliveryPhoto', true)
          controllers
            .updateClusterShippersThirdPartyConversionDeliveryInformation(clusterShipperGuid!, values)
            .then(response => {
              enqueueSnackbar('3rd Party Conversion Delivery Photo Saved Successfully!', { variant: 'success' })
              setThirdPartyConversionDeliveryPhoto(response.value)
              setDialogState('thirdPartyConversionDeliveryPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isUpdatingThirdPartyConversionDeliveryPhoto', false)
            })
        }}
        open={dialogState.thirdPartyConversionDeliveryPhotoDialogOpen}
        clusterShipperGuid={clusterShipperGuid!}
      />

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='center'>
            <Grid item xs={12}>
              <NeedsClusterDeliveredToThirdPartyConversionCompanyStepper activeStep={0} />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <NeedsThirdPartyConversionDeliveryPhotoCard
                complete={Boolean(thirdPartyConversionDeliveryPhoto.clusterShipperThirdPartyConversionDeliveryPhotoFileName)}
                fileName={thirdPartyConversionDeliveryPhoto.clusterShipperThirdPartyConversionDeliveryPhotoFileName}
                onClick={() => {
                  setDialogState('thirdPartyConversionDeliveryPhotoDialogOpen', true)
                }}
                type='third-party-conversion-delivery-photo'
                clusterShipperGuid={clusterShipperGuid}
              />
            </Grid>

            <Grid item xs={12} marginTop={2}>
              <Button
                color='primary'
                endIcon={<ArrowRight />}
                fullWidth
                onClick={onNext}
                type='submit'
                variant='contained'
                disabled={!allPhotosUploaded}
              >
                NEXT
              </Button>
            </Grid>
            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**Needs Cluster Delivered to 3rd Party Conversion Company Steps:**

Displays the current step of the cluster shipper in the Needs Cluster 
Delivered to 3rd Party Conversion Company process.

---

**3rd Party Conversion Delivery Photo:**

Displays the 3rd Party Conversion Delivery image uploaded by the user.

If no image has been uploaded, a “No Image” icon will be 
displayed instead.

Clicking the 3rd Party Conversion Delivery image will open the Capture 3rd 
Party Conversion Delivery Photo Dialog.

---

**NEXT Button:**

Clicking this button will:

- Save the uploaded photo to the database.

- Advance the user to the next step in the Needs Cluster 
Delivered to 3rd Party Conversion Company process.

This button remains disabled until a photo has been uploaded.

---
          `}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
